import React, { useEffect, useRef, useState, useCallback } from 'react'
import {
    BrowserMultiFormatReader,
    MultiFormatReader,
    DecodeHintType,
    BarcodeFormat,
    NotFoundException,
    BinaryBitmap,
    HybridBinarizer,
} from '@zxing/library'
import { HTMLCanvasElementLuminanceSource } from '@zxing/browser'
import styled from 'styled-components'
import { Button, Dialog, Icon, Typography } from '@equinor/eds-core-react'
import { close } from '@equinor/eds-icons'
import { baseFont, baseLinkStyles } from '@/styles/layout'
import { useConfirmDialog } from '@/hooks/useConfirmDialog'
import { useAtom } from 'jotai'
import { onboardedEditedAtom, selectedSensorCardAtom, sensorAdditionalDataAtom } from '@/core/store/globalStore'
import { extractDevEui } from '@/utils/vendorQrParsers'

interface ScannerProps {
    onScan: (data: any) => void
    onFetchingData?: (isLoading: boolean) => void
    scannerButton: React.ReactElement<{
        onClick?: () => void
        disabled?: boolean
    }>
    isReadSensor?: boolean
}

const vendorDomains = ['indusenz.com']

const Scanner: React.FC<ScannerProps> = ({ onScan, onFetchingData, scannerButton, isReadSensor = false }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const [qrCodeData, setQrCodeData] = useState<string | null>(null)
    const [, setFetchedContent] = useState<string | null>(null)
    const [isFetching, setIsFetching] = useState<boolean>(false)
    const [, setIsScanning] = useState<boolean>(false)
    const [permissionDenied, setPermissionDenied] = useState<boolean>(false)
    const [cameraError, setCameraError] = useState<string | null>(null)
    const [isDeviceReady, setIsDeviceReady] = useState<boolean>(false)
    const [isCameraOn, setIsCameraOn] = useState<boolean>(false)
    const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([])
    const [selectedCamera, setSelectedCamera] = useState<string | null>(null)
    const [stream, setStream] = useState<MediaStream | null>(null)

    const [, setSelectedSensorCard] = useAtom(selectedSensorCardAtom)
    const [, setSensorAdditionalData] = useAtom(sensorAdditionalDataAtom)
    const [onboardedEdited, setOnboardedEdited] = useAtom(onboardedEditedAtom)

    const hints = new Map()
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE, BarcodeFormat.DATA_MATRIX])
    hints.set(DecodeHintType.TRY_HARDER, true)

    const codeReader = useRef(new BrowserMultiFormatReader(hints)).current
    const fallbackReader = useRef(
        (() => {
            const reader = new MultiFormatReader()
            reader.setHints(hints)
            return reader
        })()
    ).current
    const sourceCanvasRef = useRef<HTMLCanvasElement | null>(null)
    const rotatedCanvasRef = useRef<HTMLCanvasElement | null>(null)

    const FALLBACK_ANGLES = [
        Math.PI / 12,
        -Math.PI / 12, // ±15°
        Math.PI / 6,
        -Math.PI / 6, // ±30°
        Math.PI / 4,
        -Math.PI / 4, // ±45°
        Math.PI / 3,
        -Math.PI / 3, // ±60°
        (5 * Math.PI) / 12,
        -(5 * Math.PI) / 12, // ±75°
        Math.PI / 2,
        -Math.PI / 2, // ±90°
    ]

    const CONTRAST_FACTOR = 1.5

    const enhanceContrast = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
        const imageData = ctx.getImageData(0, 0, w, h)
        const data = imageData.data
        const f = CONTRAST_FACTOR
        const offset = 128 - 128 * f
        for (let i = 0; i < data.length; i += 4) {
            data[i] = f * data[i] + offset // R
            data[i + 1] = f * data[i + 1] + offset // G
            data[i + 2] = f * data[i + 2] + offset // B
        }
        ctx.putImageData(imageData, 0, 0)
    }

    const tryRotatedDecode = useCallback(
        (video: HTMLVideoElement) => {
            const vw = video.videoWidth
            const vh = video.videoHeight
            if (vw === 0 || vh === 0) return null

            if (!sourceCanvasRef.current) {
                sourceCanvasRef.current = document.createElement('canvas')
            }
            const sourceCanvas = sourceCanvasRef.current
            sourceCanvas.width = vw
            sourceCanvas.height = vh
            const sourceCtx = sourceCanvas.getContext('2d')
            if (!sourceCtx) return null
            sourceCtx.drawImage(video, 0, 0, vw, vh)

            const diagonal = Math.ceil(Math.sqrt(vw * vw + vh * vh))
            if (!rotatedCanvasRef.current) {
                rotatedCanvasRef.current = document.createElement('canvas')
            }
            const canvas = rotatedCanvasRef.current
            canvas.width = diagonal
            canvas.height = diagonal
            const ctx = canvas.getContext('2d', { willReadFrequently: true })
            if (!ctx) return null

            const cx = diagonal / 2
            const cy = diagonal / 2

            try {
                const enhancedCanvas = sourceCanvasRef.current!
                const enhancedCtx = enhancedCanvas.getContext('2d')!
                enhanceContrast(enhancedCtx, vw, vh)
                const luminance = new HTMLCanvasElementLuminanceSource(enhancedCanvas)
                const bitmap = new BinaryBitmap(new HybridBinarizer(luminance))
                return fallbackReader.decodeWithState(bitmap)
            } catch (e) {
                if (!(e instanceof NotFoundException)) throw e
                sourceCtx.drawImage(video, 0, 0, vw, vh)
            }

            for (const angle of FALLBACK_ANGLES) {
                ctx.clearRect(0, 0, diagonal, diagonal)
                ctx.save()
                ctx.translate(cx, cy)
                ctx.rotate(angle)
                ctx.drawImage(sourceCanvas, -vw / 2, -vh / 2, vw, vh)
                ctx.restore()

                enhanceContrast(ctx, diagonal, diagonal)

                try {
                    const luminance = new HTMLCanvasElementLuminanceSource(canvas)
                    const bitmap = new BinaryBitmap(new HybridBinarizer(luminance))
                    return fallbackReader.decodeWithState(bitmap)
                } catch (e) {
                    if (!(e instanceof NotFoundException)) throw e
                }
            }
            return null
        },
        [fallbackReader]
    )

    const isValidURL = (data: string): boolean => {
        return vendorDomains.some((domain) => data.includes(domain))
    }

    const fetchCameras = useCallback(async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices()
            const videoDevices = devices.filter((device) => device.kind === 'videoinput')
            setAvailableCameras(videoDevices)

            if (videoDevices.length > 0) {
                const stillExists = selectedCamera && videoDevices.some((d) => d.deviceId === selectedCamera)
                if (!stillExists) setSelectedCamera(videoDevices[0].deviceId)
            }
        } catch {
            setCameraError('Unable to fetch cameras.')
        }
    }, [selectedCamera])

    const fetchContent = useCallback(
        async (url: string) => {
            setIsFetching(true)
            onFetchingData?.(true)
            setFetchedContent(null)

            try {
                const response = await fetch(url)
                if (!response.ok) {
                    throw new Error(`Failed to fetch. Status: ${response.status}`)
                }

                const json = await response.json()
                if (json && json.serial) {
                    const serialNumber = json.serial
                    setFetchedContent(JSON.stringify(json, null, 2))
                    onScan({ serialNumber, VendorSerialNumber: serialNumber })
                } else {
                    setFetchedContent('Serial number not found in JSON response.')
                }
            } catch {
                setFetchedContent('Error fetching or parsing content from the URL.')
            } finally {
                setIsFetching(false)
                onFetchingData?.(false)
            }
        },
        [onScan, onFetchingData]
    )

    const stopScanning = useCallback(() => {
        setIsScanning(false)
        codeReader.reset()
    }, [codeReader])

    const stopStream = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach((track) => track.stop())
        }
        setStream(null)
        setIsDeviceReady(false)
        if (videoRef.current) videoRef.current.srcObject = null
    }, [stream])

    const requestCameraPermission = useCallback(
        async (deviceId?: string | null) => {
            try {
                const s = await navigator.mediaDevices.getUserMedia({
                    video: deviceId ? { deviceId: { exact: deviceId } } : true,
                    audio: false,
                })

                if (videoRef.current) {
                    videoRef.current.srcObject = s
                    setStream(s)
                    setIsDeviceReady(true)
                }

                await fetchCameras()
            } catch (err) {
                if (err instanceof DOMException) {
                    if (err.name === 'NotAllowedError') {
                        setPermissionDenied(true)
                    } else {
                        setCameraError('Unable to access the camera due to permissions or device settings.')
                    }
                } else if (err instanceof Error) {
                    setCameraError(`An error occurred: ${err.message}`)
                }
            }
        },
        [fetchCameras]
    )

    const startScanning = useCallback(() => {
        if (!videoRef.current) return
        setIsScanning(true)

        const constraints: MediaStreamConstraints = {
            video: selectedCamera
                ? {
                      deviceId: { exact: selectedCamera },
                      width: { ideal: 1280 },
                      height: { ideal: 720 },
                  }
                : {
                      width: { ideal: 1280 },
                      height: { ideal: 720 },
                  },
            audio: false,
        }

        const handleScannedData = (scannedData: string) => {
            setQrCodeData(extractDevEui(scannedData))
            onScan({ serialNumber: extractDevEui(scannedData) })

            if (isValidURL(scannedData)) {
                const validUrl = scannedData.startsWith('https://') ? scannedData : `https://${scannedData}`
                fetchContent(validUrl)
            }
        }

        codeReader
            .decodeFromConstraints(constraints, videoRef.current, (result, err) => {
                if (result) {
                    handleScannedData(result.getText())
                    doToggleCamera()
                    return
                }

                if (err instanceof NotFoundException) {
                    if (!videoRef.current) return
                    const rotatedResult = tryRotatedDecode(videoRef.current)
                    if (rotatedResult) {
                        handleScannedData(rotatedResult.getText())
                        doToggleCamera()
                    }
                } else if (err) {
                    // keep quiet unless you want to show errors
                }
            })
            .catch((err) => {
                if (err instanceof Error) {
                    setCameraError(`Scanning error: ${err.message}`)
                } else {
                    setCameraError('An unknown scanning error occurred.')
                }
            })
    }, [selectedCamera, codeReader, fetchContent, onScan])

    const { showConfirm } = useConfirmDialog({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to proceed?',
        rejectButtonTitle: 'Discard changes',
        confirmButtonTitle: 'Stay on page',
        onReject: () => {
            setSelectedSensorCard(null)
            setSensorAdditionalData([])
            setOnboardedEdited(false)
            doToggleCamera()
        },
        onConfirm: () => {},
        autoClose: true,
    })

    const showCamera = () => {
        if (onboardedEdited) {
            showConfirm()
            return
        }
        doToggleCamera()
    }

    const doToggleCamera = () => {
        if (isCameraOn) {
            setIsCameraOn(false)
            stopScanning()
            stopStream()
        } else {
            setQrCodeData(null)
            setFetchedContent(null)
            setCameraError(null)
            setPermissionDenied(false)
            setIsCameraOn(true)
            requestCameraPermission(selectedCamera)
        }
    }

    const flipCamera = useCallback(() => {
        if (availableCameras.length > 1) {
            const currentIndex = availableCameras.findIndex((cam) => cam.deviceId === selectedCamera)
            const safeIndex = currentIndex >= 0 ? currentIndex : 0
            const nextIndex = (safeIndex + 1) % availableCameras.length
            const nextId = availableCameras[nextIndex].deviceId
            setSelectedCamera(nextId)

            if (isCameraOn) {
                stopScanning()
                stopStream()
                setTimeout(() => requestCameraPermission(nextId), 0)
            }
        }
    }, [availableCameras, selectedCamera, isCameraOn, stopScanning, stopStream, requestCameraPermission])

    useEffect(() => {
        if (isDeviceReady && isCameraOn && !qrCodeData) {
            startScanning()
        }
        return () => {
            stopScanning()
        }
    }, [isDeviceReady, isCameraOn, qrCodeData, startScanning, stopScanning])

    useEffect(() => {
        return () => {
            stopScanning()
            stopStream()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div>
            {React.cloneElement(scannerButton, { onClick: showCamera, disabled: isFetching })}
            {isCameraOn && (
                <ScannerContainer color="black" open={isCameraOn} isDismissable onClose={() => setIsCameraOn(false)}>
                    <Header>
                        <ScannerStyledLabel>Scan QR</ScannerStyledLabel>
                        <CloseButton as="Link" onClick={doToggleCamera}>
                            <ScannerStyledCloseLabel>Close</ScannerStyledCloseLabel>
                            <Icon data={close} size={24} style={{ color: '#007079' }} />
                        </CloseButton>
                    </Header>

                    <VideoContainer
                        ref={videoRef}
                        style={{ width: '100%', height: '100%', background: '#000' }}
                        autoPlay
                        muted
                        playsInline
                    />

                    <Bottom>
                        <ScannerStyledLinkButton as="Link" onClick={flipCamera}>
                            <Label>Flip camera</Label>
                        </ScannerStyledLinkButton>
                    </Bottom>

                    {permissionDenied && <p style={{ color: 'red' }}>Permission denied. Please allow camera access.</p>}
                    {cameraError && <p style={{ color: 'red' }}>{cameraError}</p>}
                </ScannerContainer>
            )}
        </div>
    )
}

const Label = styled.span`
    ${baseFont}
    @media (max-width: 1024px) {
        &:hover {
            background: #f5f5f5;
            color: #1f6369;
        }
    }
`

const ScannerStyledLinkButton = styled(Button)`
    ${baseLinkStyles}
    background: white;
    color: #007079;
    border-radius: 4px;
    border: 1px solid;
    border-color: #007079;

    &:hover {
        color: #1c1c1c;
        background: #f7f7f7;
    }

    @media (max-width: 1024px) {
        background: #ffffff;
        color: #1f6369;
        border-color: #21767e;

        &:hover {
            background: #f5f5f5;
            color: #1f6369;
            border-color: #21767e;
        }
    }
`

const ScannerStyledLabel = styled(Typography)`
    ${baseFont}
    line-height: 24px;
    letter-spacing: 0.2px;
    color: #3d3d3d;
`

const ScannerStyledCloseLabel = styled(Typography)`
    ${baseFont}
    color: #007079;
`

const ScannerContainer = styled(Dialog)`
    display: flex;
    flex-direction: column;
    padding: 8px 8px;
    align-items: center;
    width: 550px;

    @media (max-width: 1024px) {
        width: 340px;
        height: 500px;
        max-height: 550px;
    }
`
const VideoContainer = styled.video`
    padding: 0;
    margin: 0;

    @media (max-width: 1024px) {
        max-height: 370px;
    }
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-bottom: 0px;
`

const Bottom = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    margin-top: 0px;
    margin-bottom: 4px;
`

const CloseButton = styled(Button)`
    ${baseFont}
    !color: #007079;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
        background: white;
    }

    @media (max-width: 1024px) {
        color: #007079;

        &:hover {
            background: white;
        }
    }
`

export default Scanner
